import React, { } from 'react';
import { ResourceStrings } from '../../../ResourceStrings';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { SearchInfo, OperationState } from '../../../store/appState';
import { SearchResult } from '../../../SearchResults';
import styled from 'styled-components/macro';
import { App } from '../../../app';
import { useHistory } from 'react-router-dom';
import { History } from "history";
import { DetailViewType, UrlParam } from '../../../constants/urlParams';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import Flex from '../../shared/Flex';
import RichTicketDescription from '../Details/RichTicketDescription';
import { isNeedMoreInfoVisible } from './MyFeedbackView';
import { getMostRelevantFixedInRelease } from '../../../hooks/posts';
import { PostId, PostType, PrincipalId, ReactionCounts, RootPost, StateTag, Tag, TagCategory } from '../../../RootPost';
import { CommentType } from '../../../PublicFeedback';
import { isClosedState, stateTagIdFromState } from '../../../constants/feedback';
import { SpaceId } from '../../../config/spaces/spaceIds';

export interface FeedbackListProps {
    feedbackList: SearchInfo;
    itemsFilter?: (items: SearchResult[]) => SearchResult[];
    forNMIAction?: boolean;
    clearResultsWhenfetching?: boolean;     // Temporary prop for search page to avoid breaking existing components (until proper paging is implemented)
}

export default function FeedbackList(props: FeedbackListProps): JSX.Element {
    const history = useHistory();
    const signInState = useSelector((state: RootState) => state.appState.signInState);
    const userRoles = useSelector((state: RootState) => state.appState.userRoles);
    const userProperties = useSelector((state: RootState) => state.appState.userProperties);

    let list = props.feedbackList.results.results ?? [];

    if (props.itemsFilter) {
        list = props.itemsFilter(list);
    }

    const fillMissingInfoClicked = (post: SearchResult, history: History): void => {
        const detailsUrl = App.getTicketPathWithoutParams(post.answerHubId, post.title);
        const urlParams = new URLSearchParams(history.location.search);
        urlParams.set(UrlParam.ProvideMoreInfo, "true");
        urlParams.set(UrlParam.DetailViewType, DetailViewType.all);
        history.push(`${detailsUrl}?${urlParams.toString()}`);
    }

    const showList = props.clearResultsWhenfetching
        ? props.feedbackList.state === OperationState.Success
        : props.feedbackList.state !== OperationState.Failed;   // for paging scenario on my feedback page: don't hide already loaded items

    return <Wrapper>
        {props.feedbackList.state === OperationState.Success && list.length === 0 && <div>{ResourceStrings.noItemsFound()}</div>}
        {props.feedbackList.state === OperationState.Executing && <Flex justifyContent="center" className="fetching">
            <Spinner size={SpinnerSize.small} />
            <span>&nbsp;&nbsp;Fetching results...</span>
        </Flex>}
        {props.feedbackList.state === OperationState.Failed && <div className="error">
            There was an error in retrieving the search results.
            <pre>{props.feedbackList.errorMessage}</pre>
        </div>}

        {showList && list.map((item, i) => (
            <RichTicketDescription
                position={i}
                key={item.answerHubId}
                ticket={rootPostFromSearchResult(item)}
                userRoles={userRoles}
                userProperties={userProperties}
                hideVotes={false}
                signInState={signInState}
                showMinimalView
                mostRelevantFixedInRelease={getMostRelevantFixedInRelease(item.tags)}
                forNMIAction={props.forNMIAction}
                showNeedMoreInfoLink={isNeedMoreInfoVisible(item, userProperties)}
                fillMissingInfoClicked={() => fillMissingInfoClicked(item, history)}
            />
        ))}
    </Wrapper>
}

function rootPostFromSearchResult(item: SearchResult): RootPost {
    const postType = item.type === CommentType.Issue ? PostType.problem : PostType.suggestion;
    return {
        author: {
            id: -1 as PrincipalId,
            name: item.createdBy,
        },
        id: item.answerHubId as PostId,
        ai: null,
        cantComment: false,
        type: postType,
        createdDate: new Date(item.createdDateUtc),
        cantVote: isClosedState(item.state) || item.iCreatedThis,
        children: [],
        commentCount: item.commentCount,
        createdByImageLink: item.createdByImageLink,
        createdByUrl: item.createdByUrl,
        hasVoted: item.hasVoted,
        iCreatedThis: item.iCreatedThis,
        isClean: true,
        isDeleted: false,
        isFollowing: item.isFollowing,
        lastActivityDate: new Date(item.lastModifiedUtc),
        partialHistory: [],
        permittedViewers: null,
        reactCounts: ReactionCounts.zero(),
        ownReactCounts: ReactionCounts.zero(),
        solutionCount: item.solutionCount,
        spaceId: item.spaceId,
        state: item.state,
        stateTagId: stateTagIdFromState(item.state, postType),
        tags: item.tags.map<Tag>(tag => { return { id: 0, category: TagCategory.generic, type: "generic", value: tag.value } }),
        text: item.text,
        title: item.title,
        totalVotes: item.totalVotes,
        trackingId: item.trackingId,
        currentUserId: -1 as PrincipalId,
        isPublic: item.spaceId !== SpaceId.Internal,    // TODO: Better get this as part of results from search API itself
        intUrl: item.intUrl,
    };
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;

    >* + * {
        margin-top: 1rem;
    }

    .fetching {
        margin-top: 6rem;
    }
`;
